import React, { useEffect, useState } from "react";
import styles from "./GiftWithPurchase.module.scss";
import AddToBasketButton from "@JohnLewisPartnership/jl-design-system/dist/components/add-to-basket-button/AddToBasketButton";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";
import { GiftWithPurchaseColours } from "./GiftWithPurchaseColours/GiftWithPurchaseColours";
import { hasSizes } from "@/utils/product/hasSizes";
import { GiftWithPurchaseSizes } from "./GiftWithPurchaseSizes/GiftWithPurchaseSizes";
import { Product, Variant, VariantGroup } from "../../../../types/types";

type GiftWithPurchaseProps = {
  targetProduct: Product;
  promoCode: string;
  drawIsOpen: boolean;
  basketEndpoint: string;
  isGiftOutOfStock: boolean;
};

type GiftWithPurchaseColourOption = {
  colour: string;
  showAsAvailable: boolean;
  colourSwatch: string | null | undefined;
  selected: boolean;
  imageUrl?: string;
};

const isMobileWidth = () => window.innerWidth < 768;

const getVariantsForGroup = (variantGroup: VariantGroup, product: Product) => {
  const groupVariantIds = variantGroup.variants.map((v) => v.id);
  const allVariants: Variant[] = product.variants;
  const groupVariants = allVariants.filter((v) =>
    groupVariantIds.includes(v.id),
  );
  return groupVariants;
};

const GiftWithPurchase: React.FC<GiftWithPurchaseProps> = (
  giftWithPurchaseProps: GiftWithPurchaseProps,
) => {
  const { targetProduct, basketEndpoint, isGiftOutOfStock } =
    giftWithPurchaseProps;

  const variantGroupsHaveColours = !!targetProduct.variantGroups?.[0]?.value;
  const productHasSizes = hasSizes(targetProduct);

  const [addToBasketIsClicked, setAddToBasketIsClicked] = useState(false);
  const [selectedVariantGroup, setSelectedVariantGroup] = useState<
    VariantGroup | undefined
  >(undefined);
  const [selectedVariant, setSelectedVariant] = useState<Variant | undefined>(
    undefined,
  );
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(isMobileWidth());
    const handleResize = () =>
      isMobileWidth() ? setIsMobile(true) : setIsMobile(false);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (variantGroupsHaveColours) {
      if (!productHasSizes) {
        const matchingVariant = targetProduct.variants?.filter(
          (variant) =>
            variant.id === targetProduct.variantGroups?.[0]?.variants[0]?.id,
        )[0];
        setSelectedVariant(matchingVariant);
      }
    } else {
      setSelectedVariant(targetProduct?.variants[0]);
    }
  }, []);

  useEffect(() => {
    if (variantGroupsHaveColours || productHasSizes) {
      if (colourOptions.length === 1) {
        setSelectedVariantGroup(targetProduct?.variantGroups?.[0]);
      }
      if (!productHasSizes) {
        const matchingVariant = targetProduct.variants?.filter(
          (variant) => variant.id === selectedVariantGroup?.variants[0]?.id,
        )[0];
        setSelectedVariant(matchingVariant);
      } else {
        setSelectedVariant(undefined);
      }
    } else {
      setSelectedVariant(targetProduct.variants?.[0]);
    }
  }, [selectedVariantGroup]);

  const colourOptions: GiftWithPurchaseColourOption[] =
    targetProduct.variantGroups?.map((variantGroup) => {
      const colour = variantGroup.value || "";

      const groupVariants = getVariantsForGroup(variantGroup, targetProduct);

      const isAvailable = groupVariants.some(
        (v) =>
          (v.availability?.availableToOrder &&
            !v.availability?.shipFromStore) ||
          v.availability?.unavailableReason === "inStoreOnly",
      );

      return {
        colour,
        showAsAvailable: isAvailable,
        colourSwatch: groupVariants[0]?.colourSwatch,
        selected: selectedVariantGroup?.value === colour,
        imageUrl: groupVariants[0]?.images.primary,
      };
    }) || [];

  const variantGroupIsUnavailable = selectedVariantGroup
    ? colourOptions.filter(
        (colourOption) => colourOption.colour === selectedVariantGroup.value,
      )[0]?.showAsAvailable === false
    : true;

  const getAddToBasketWording = () => {
    if (isGiftOutOfStock) {
      return "Out of stock";
    } else if (colourOptions.length > 1 && variantGroupIsUnavailable) {
      return "Out of stock";
    } else if (
      productHasSizes &&
      selectedVariant &&
      !selectedVariant.availability?.availableToOrder
    ) {
      return "Out of stock";
    } else if (
      ((variantGroupsHaveColours && selectedVariantGroup) ||
        colourOptions.length <= 1) &&
      !selectedVariant &&
      productHasSizes
    ) {
      return "Choose a size first";
    } else if (addToBasketIsClicked) {
      return "Added to basket";
    } else {
      return undefined;
    }
  };

  const getDisplayPrice = () => {
    if (productHasSizes && selectedVariant) {
      return selectedVariant.price?.display;
    } else if (variantGroupsHaveColours && selectedVariantGroup) {
      if (
        selectedVariantGroup.variantPriceRange?.display.min ===
        selectedVariantGroup.variantPriceRange?.display.max
      ) {
        return selectedVariantGroup.variantPriceRange?.display.min;
      } else {
        return `${selectedVariantGroup.variantPriceRange?.display.min} - ${selectedVariantGroup.variantPriceRange?.display.max}`;
      }
    } else {
      const singleVariant: Variant | undefined = targetProduct?.variants[0];
      return singleVariant?.price?.display;
    }
  };

  const getImageUrl = () => {
    if (productHasSizes && selectedVariant) {
      return selectedVariant.images.primary;
    } else if (variantGroupsHaveColours && selectedVariantGroup) {
      return colourOptions.filter(
        (colourOption) => colourOption.colour === selectedVariantGroup.value,
      )[0]?.imageUrl;
    } else {
      return targetProduct?.variants[0]?.images.primary;
    }
  };

  const AddGiftToBasketButton = () => {
    return (
      <AddToBasketButton
        path={`${basketEndpoint}/items`}
        className={
          isGiftOutOfStock
            ? styles.addToBasketButtonDisabled
            : styles.addToBasketButton
        }
        skuId={selectedVariant?.aliases?.skuId}
        variantId={selectedVariant?.id}
        clientId="web_pdp_scaffold_ui"
        title={targetProduct?.title}
        quantity={1}
        disabled={
          isGiftOutOfStock ||
          !selectedVariant ||
          !selectedVariant?.availability?.availableToOrder ||
          selectedVariant?.availability.shipFromStore
        }
        customText={getAddToBasketWording()}
        onSuccess={() => {
          setAddToBasketIsClicked(true);
          dispatchMonetateQEvent("GIFT_WITH_PURCHASE_ATB_BUTTON_CLICKED");
        }}
      />
    );
  };

  if (!basketEndpoint) return null;

  return (
    <div className={styles.container}>
      <div className={styles.imageAndInfoContainer}>
        <a
          href={targetProduct.pdpURL.url}
          target="_blank"
          rel="noreferrer"
          data-testid="gift-with-purchase-figure"
        >
          <figure className={styles.productImage}>
            <img
              src={formatAmplienceImageURL(getImageUrl() || "")}
              alt={targetProduct?.title}
            ></img>
          </figure>
        </a>

        <div className={styles.productTitleAndButtonContainer}>
          <a
            href={targetProduct.pdpURL.url}
            target="_blank"
            rel="noreferrer"
            data-testid="gift-with-purchase-title"
          >
            <div className={styles.productBrand}>
              {targetProduct?.brand?.name}
            </div>
            <div className={styles.productTitle}>{targetProduct?.title}</div>
            <div
              className={styles.productPrice}
              data-testid="gift-with-purchase-price"
            >
              Usual price:&nbsp;
              <strong>{getDisplayPrice()}</strong>
            </div>
            {colourOptions?.length > 1 && (
              <p>
                Colour:&nbsp;
                <strong>{selectedVariantGroup?.value || ""}</strong>
              </p>
            )}
            {productHasSizes && (
              <p>
                Size:&nbsp;
                <strong>{selectedVariant?.differentiators?.size || ""}</strong>
              </p>
            )}
          </a>
          <div className={styles.buttonContainer}>
            {AddGiftToBasketButton()}
          </div>
        </div>
      </div>
      {(variantGroupsHaveColours || productHasSizes) && (
        <div className={styles.coloursAndSizesContainer}>
          {colourOptions.length > 1 && (
            <GiftWithPurchaseColours
              colourOptions={colourOptions}
              selectedVariantGroup={selectedVariantGroup}
              setSelectedVariantGroup={setSelectedVariantGroup}
              product={targetProduct}
              isMobile={isMobile}
            />
          )}
          {productHasSizes && (
            <GiftWithPurchaseSizes
              selectedVariantGroup={selectedVariantGroup}
              selectedVariant={selectedVariant}
              setSelectedVariant={setSelectedVariant}
              product={targetProduct}
              isMobile={isMobile}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default GiftWithPurchase;
